// Utils
import { DISCLAIMER_KEYS } from './constants';
import helpers from './helpers';
import { ASSET_TYPE } from '../constants';
import { LOCALE, formatMessage } from '../i18n';
import Dictionary from '@/types/dictionary';
import Disclaimer from '@/types/disclaimer';
import Fund from '@/types/fund';
import Benchmark from '@/types/benchmark';

/**
 * Generates the disclaimers to be used inside a comparison for a fund.
 * @param fund Fund used to generate the disclaimers.
 * @param benchmarks Benchmarks data associated with the specified fund.
 */
export const getComparisonFundDisclaimers = (fund: Fund, benchmarks?: Benchmark[]) => {
  const disclaimers: Dictionary<Disclaimer> = {};
  const firstDataDate = helpers.getFirstDataPointDate(fund);
  const lastDataDate = helpers.getLastDataPointDate(fund);
  const lastNavDataDate = helpers.getLastNavDataPointDate(fund);

  // Generic disclaimer with the date of the first data point -- used for time-based data fields.
  disclaimers[DISCLAIMER_KEYS.FUND_NO_DATA_BEFORE_DATE] = {
    key: DISCLAIMER_KEYS.FUND_NO_DATA_BEFORE_DATE,
    data: {
      date: firstDataDate,
    },
  };

  const fundDisclaimers = helpers.getFundDisclaimers(fund);
  if (fundDisclaimers.length > 0) {
    disclaimers[DISCLAIMER_KEYS.INSTRUMENT_CUSTOM_DISCLAIMER] = {
      key: DISCLAIMER_KEYS.INSTRUMENT_CUSTOM_DISCLAIMER,
      data: {
        holdings: fundDisclaimers,
      },
    };
  }

  if (helpers.holdingIsClosed(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_CLOSED] = {
      key: DISCLAIMER_KEYS.FUND_CLOSED,
      data: {
        date: lastDataDate,
      },
    };
  }

  if (helpers.holdingNavIsClosed(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_NAV_CLOSED] = {
      key: DISCLAIMER_KEYS.FUND_NAV_CLOSED,
      data: {
        date: lastNavDataDate,
      },
    };
  }

  // Disclaimers related to missing data
  if (helpers.holdingHasNoData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_NO_DATA] = {
      key: DISCLAIMER_KEYS.FUND_NO_DATA,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_NO_RISK_METRICS] = {
      key: DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
    };
  }

  if (helpers.holdingHasNoNavData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_NO_NAV_DATA] = {
      key: DISCLAIMER_KEYS.FUND_NO_NAV_DATA,
    };
  }

  if (helpers.holdingHasLessThan1YData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_LESS_THAN_1Y] = {
      key: DISCLAIMER_KEYS.FUND_LESS_THAN_1Y,
      data: {
        date: firstDataDate,
      },
    };

    disclaimers[DISCLAIMER_KEYS.FUND_NO_RISK_METRICS] = {
      key: DISCLAIMER_KEYS.FUND_NO_RISK_METRICS,
    };
  }

  if (helpers.holdingHasLessThan3YData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_LESS_THAN_3Y] = {
      key: DISCLAIMER_KEYS.FUND_LESS_THAN_3Y,
      data: {
        date: firstDataDate,
      },
    };
  }

  // Disclaimers related to missing fund benchmarks
  if (!benchmarks || !helpers.fundHasAllBenchmarkData(fund, benchmarks)) {
    const MISSING_BENCHMARK_DISCLAIMERS = [
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_SHARPE,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_SORTINO,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_UPDOWN,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_BETA,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_TRACKING_ERROR,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_ALPHA,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_INFO_RATIO,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_CORRELATION_COEFFICIENT,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK_RSQUARED,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK,
    ];

    for (const key of MISSING_BENCHMARK_DISCLAIMERS) {
      disclaimers[key] = { key };
    }
  }

  // Disclaimers related to fund having the same benchmark

  if (helpers.fundHasSameBenchmark(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_HAS_SAME_BENCHMARK] = {
      key: DISCLAIMER_KEYS.FUND_HAS_SAME_BENCHMARK,
    };
  }

  // Allocation disclaimers
  if (!helpers.fundHasAllTopHoldingsData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_MISSING_TOP_HOLDINGS] = {
      key: DISCLAIMER_KEYS.FUND_MISSING_TOP_HOLDINGS,
    };
  }
  if (!helpers.fundHasAllCountryAllocationData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_MISSING_COUNTRY_ALLOC] = {
      key: DISCLAIMER_KEYS.FUND_MISSING_COUNTRY_ALLOC,
    };
  }

  if (helpers.fundHasCreditSecurities(fund)) {
    if (!helpers.fundHasAllMaturityAllocationData(fund)) {
      disclaimers[DISCLAIMER_KEYS.FUND_MISSING_MATURITY] = {
        key: DISCLAIMER_KEYS.FUND_MISSING_MATURITY,
      };
    }

    if (!helpers.fundHasAllCreditQualityAllocationData(fund)) {
      disclaimers[DISCLAIMER_KEYS.FUND_MISSING_CREDIT_QUALITY] = {
        key: DISCLAIMER_KEYS.FUND_MISSING_CREDIT_QUALITY,
      };
    }

    if (!helpers.fundHasAllFixedIncomeStyleData(fund)) {
      disclaimers[DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_STYLE] = {
        key: DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_STYLE,
      };
    }

    if (!helpers.fundHasAllFixedIncomeRegionsAllocationData(fund)) {
      disclaimers[DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_REGIONS] = {
        key: DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_REGIONS,
      };
    }

    if (!helpers.fundHasAllFixedIncomeSectorAllocationData(fund)) {
      disclaimers[DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_SECTORS] = {
        key: DISCLAIMER_KEYS.FUND_MISSING_FIXED_INCOME_SECTORS,
      };
    }
  }

  if (!helpers.fundHasAllAssetDetailsData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_MISSING_ASSET_DETAILS] = {
      key: DISCLAIMER_KEYS.FUND_MISSING_ASSET_DETAILS,
    };
  }

  if (fund.assetType && fund.assetType[LOCALE.EN_CA] === ASSET_TYPE.BOND) {
    disclaimers[DISCLAIMER_KEYS.FUND_BOND_INDUSTRY_HOLDINGS] = {
      key: DISCLAIMER_KEYS.FUND_BOND_INDUSTRY_HOLDINGS,
    };
  }

  if (!helpers.fundHasAllIndustryHoldingsData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_MISSING_INDUSTRY_HOLDINGS] = {
      key: DISCLAIMER_KEYS.FUND_MISSING_INDUSTRY_HOLDINGS,
    };
  }

  if (!helpers.fundHasCreditSecurities(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_CREDIT_QUALITY_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_CREDIT_QUALITY_NOT_APPLICABLE,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_MATURITY_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_MATURITY_NOT_APPLICABLE,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_METRICS_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_FIXED_INCOME_METRICS_NOT_APPLICABLE,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_REGION_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_FIXED_INCOME_REGION_NOT_APPLICABLE,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_SECTORS_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_FIXED_INCOME_SECTORS_NOT_APPLICABLE,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_FIXED_INCOME_STYLE_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_FIXED_INCOME_STYLE_NOT_APPLICABLE,
    };
  }

  if (!helpers.fundHasEquity(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_EQUITY_METRICS_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_EQUITY_METRICS_NOT_APPLICABLE,
    };

    disclaimers[DISCLAIMER_KEYS.FUND_EQUITY_STYLE_NOT_APPLICABLE] = {
      key: DISCLAIMER_KEYS.FUND_EQUITY_STYLE_NOT_APPLICABLE,
    };
  } else if (!helpers.fundHasAllEquityStyleData(fund)) {
    disclaimers[DISCLAIMER_KEYS.FUND_MISSING_EQUITY_STYLE] = {
      key: DISCLAIMER_KEYS.FUND_MISSING_EQUITY_STYLE,
    };
  }

  disclaimers[DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA] = {
    key: DISCLAIMER_KEYS.RISK_METRICS_NOT_ENOUGH_DATA,
  };

  fund.customProviders?.forEach(provider => {
    if (provider.fields.includes('riskRating')) {
      disclaimers[DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING] = {
        key: DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING,
        data: {
          provider: formatMessage(`Providers.${provider.key}`),
        },
      };
    }
  });

  return disclaimers;
};
